import { useEffect, useState } from "react";
import {
  CONFIRM_PHRASE,
  IDLE_VOICE_COMMANDS,
  TRANSPORT_COMMAND_PHRASES,
  VOICE_SWITCH_LABEL,
  voiceCommandStatusLabel,
  WAKE_WORD,
} from "@cuekards/core/voice-commands";
import type { VoiceCommandState } from "@cuekards/core/voice-commands";

/**
 * Voice commands in the control window (ticket 15, FR-54 to FR-56).
 *
 * The panel is mostly there to say *no*: the switch is off, and the two things
 * that can keep it from arming — a script that would trigger a command, and a
 * take waiting for its stop to be confirmed — are stated here rather than left
 * for the user to work out from a prompter that did something unexpected.
 *
 * The phrase list is not decoration, and it is shown whether the switch is on or
 * off. The commands only work if you say them exactly, so this is the one screen you
 * can read before you start filming — and it has to answer "what would I be able to
 * say?" for someone who has not turned the feature on yet.
 */
export function VoicePanel(): JSX.Element {
  const [state, setState] = useState<VoiceCommandState>(IDLE_VOICE_COMMANDS);

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getVoiceCommandState().then((s) => {
      if (!cancelled) setState(s);
    });
    const off = window.cuekards.onVoiceCommandStateChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  const collided = state.collision !== undefined;

  return (
    // A settings screen (the card chrome is drawn by whatever holds it — the
    // desktop's settings card, the web's settings page) with its own rows on
    // top. The lower half of the desktop's Shortcuts tab, under the keys.
    <section className="settings-screen voice">
      <div className="voice-row">
        <h2>Voice commands</h2>
        <span
          className={`voice-state${state.awaitingConfirmation ? " awaiting" : ""}`}
          role="status"
        >
          {voiceCommandStatusLabel(state)}
        </span>
      </div>

      <label className="voice-toggle">
        <input
          type="checkbox"
          checked={state.enabled}
          onChange={(e) => window.cuekards.setVoiceCommandsEnabled(e.target.checked)}
        />
        {/* Core's sentence, because a reader who sets this up on the desktop and
          * again on their phone is entitled to the same one twice. */}
        {VOICE_SWITCH_LABEL}
      </label>

      {/* FR-55, stated with the phrase in it. The user can only act on this if
        * they know which line to reword. */}
      {collided && (
        <p className="voice-collision" role="alert">
          {state.collision}
        </p>
      )}

      {/* Always shown, switch on or off. It is the answer to "what can I say?",
        * which is the question you have *before* deciding to turn this on — and a
        * panel that was one lone checkbox until you ticked it gave no way to find
        * out. Dimmed while off, because none of it is being listened for. */}
      <ul className={`voice-phrases${state.enabled && !collided ? "" : " idle"}`}>
        {TRANSPORT_COMMAND_PHRASES.map((phrase) => (
          <li key={phrase.command}>
            <code>
              {WAKE_WORD} {phrase.words.join(" ")}
            </code>
          </li>
        ))}
      </ul>

      {/* FR-56. Said here as well as on the overlay, because five seconds is not
        * long enough to be reading it for the first time. */}
      <p className="voice-note">
        Stop asks first: say <code>{CONFIRM_PHRASE}</code> within five seconds or
        the take keeps rolling.
      </p>
      {/* The consequence of FR-31: there is one microphone and it is opened for a
        * take or for a rehearsal, so those are the only times anything can be
        * heard. Worth stating plainly — "cuekards start" is the one command you would
        * reach for *before* either, and it is the one that needs a rehearsal
        * running to work. */}
      <p className="voice-note">
        Heard only while the microphone is open — during a take, or while
        rehearsing.
      </p>
    </section>
  );
}
